import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  DocumentIcon,
  DocumentTextIcon,
  PhotoIcon,
  CalendarIcon,
  CurrencyEuroIcon,
  MagnifyingGlassIcon,
  CheckIcon,
  ArrowPathIcon,
  UserIcon,
  TagIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';

interface LinkedBooking {
  buchung_id: number;
  konto_id: number;
  buchungstag: string;
  name_zahlungsbeteiligter: string;
  buchungstext: string;
  verwendungszweck: string;
  betrag: number;
  saldo: number;
  von_konto_id: number | null;
  nach_konto_id: number | null;
}

interface Document {
  id: number;
  name: string;
  file_path?: string;
  created_at: string;
  member_id?: string;
  member_name?: string;
  size: number;
  tag?: string;
  ai_comment?: string;
  invoice_amount?: number;
  currency?: string;
  linked_bookings?: LinkedBooking[];
  is_linked?: number;
}

interface InvoiceMatcherProps {
  onFilesSelected: (files: Document[]) => void;
  initialAmount?: number;
  preSelectedDocuments?: number[];
  onClose: () => void;
}

const InvoiceMatcher: React.FC<InvoiceMatcherProps> = ({
  onFilesSelected,
  initialAmount = 0,
  preSelectedDocuments = [],
  onClose
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [filteredDocs, setFilteredDocs] = useState<Document[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<Set<number>>(new Set());
  const [isVisible, setIsVisible] = useState(false);
  // Show all documents if we have pre-selected ones (they might be already linked)
  const [showOnlyUnlinked, setShowOnlyUnlinked] = useState(preSelectedDocuments.length === 0);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get('/api/documents');

      if (response.data) {
        setDocuments(response.data);
      }
    } catch (err: any) {
      console.error('Fehler beim Laden der Dokumente:', err);
      setError(err.message || 'Fehler beim Laden der Dokumente');
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter documents based on search term and link status
  useEffect(() => {
    let filtered = [...documents];

    // Filter by link status
    if (showOnlyUnlinked) {
      filtered = filtered.filter(doc =>
        !doc.is_linked && (!doc.linked_bookings || doc.linked_bookings.length === 0)
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(doc =>
        doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.member_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.tag?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.ai_comment?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by name
    filtered.sort((a, b) => a.name.localeCompare(b.name));

    setFilteredDocs(filtered);
  }, [documents, searchTerm, showOnlyUnlinked]);

  // Load data when component mounts
  useEffect(() => {
    fetchDocuments();
    setTimeout(() => setIsVisible(true), 10);
  }, [fetchDocuments]);

  // Set pre-selected documents when they are provided or documents are loaded
  useEffect(() => {
    if (preSelectedDocuments.length > 0 && documents.length > 0) {
      // Filter to only include document IDs that exist in the current documents list
      const validPreSelected = preSelectedDocuments.filter(id =>
        documents.some(doc => doc.id === id)
      );
      setSelectedDocuments(new Set(validPreSelected));
    }
  }, [preSelectedDocuments, documents]);

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getDocumentIcon = (filePath?: string) => {
    if (!filePath) return <DocumentIcon className="h-5 w-5 text-gray-500" />;

    if (filePath.endsWith('.pdf')) {
      return <DocumentTextIcon className="h-5 w-5 text-error-500" />;
    } else if (filePath.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      return <PhotoIcon className="h-5 w-5 text-info-500" />;
    }
    return <DocumentIcon className="h-5 w-5 text-gray-500" />;
  };

  const handleDocumentToggle = (doc: Document) => {
    const newSelected = new Set(selectedDocuments);
    if (newSelected.has(doc.id)) {
      newSelected.delete(doc.id);
    } else {
      newSelected.add(doc.id);
    }
    setSelectedDocuments(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedDocuments.size === filteredDocs.length) {
      setSelectedDocuments(new Set());
    } else {
      setSelectedDocuments(new Set(filteredDocs.map(doc => doc.id)));
    }
  };

  const handleConfirm = () => {
    const selected = documents.filter(doc => selectedDocuments.has(doc.id));
    onFilesSelected(selected);
    onClose();
  };

  // Calculate total amount of selected documents
  const calculateSelectedAmount = () => {
    let total = 0;
    selectedDocuments.forEach(docId => {
      const doc = documents.find(d => d.id === docId);
      if (doc?.invoice_amount) {
        total += doc.invoice_amount;
      }
    });
    return total;
  };

  const selectedAmount = calculateSelectedAmount();
  const remainingAmount = Math.abs(initialAmount) - selectedAmount;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl transform transition-all duration-300 ${
        isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
      }`}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-info-50 dark:bg-info-900/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-info-500 rounded-lg shadow-md">
                <LinkIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Dokumente verknüpfen
                </h2>
                <div className="flex items-center gap-4 text-sm">
                  {initialAmount !== 0 && (
                    <span className="text-gray-600 dark:text-gray-400">
                      Buchung: <span className="font-semibold">{formatCurrency(Math.abs(initialAmount))}</span>
                    </span>
                  )}
                  {selectedAmount > 0 && (
                    <span className="text-info-600 dark:text-info-400">
                      Ausgewählt: <span className="font-semibold">{formatCurrency(selectedAmount)}</span>
                    </span>
                  )}
                  {initialAmount !== 0 && selectedAmount > 0 && (
                    <span className={`font-semibold ${
                      remainingAmount === 0
                        ? 'text-success-600 dark:text-success-400'
                        : remainingAmount > 0
                        ? 'text-warning-600 dark:text-warning-400'
                        : 'text-error-600 dark:text-error-400'
                    }`}>
                      {remainingAmount === 0
                        ? '✓ Passt'
                        : remainingAmount > 0
                        ? `Fehlt: ${formatCurrency(remainingAmount)}`
                        : `Zu viel: ${formatCurrency(Math.abs(remainingAmount))}`
                      }
                    </span>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Search Bar and Filters */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Dokumente suchen..."
                className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-info-500"
              />
            </div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={showOnlyUnlinked}
                onChange={(e) => setShowOnlyUnlinked(e.target.checked)}
                className="w-4 h-4 text-info-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-info-500"
              />
              Nur unverknüpfte
            </label>
            <button
              onClick={handleSelectAll}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              {selectedDocuments.size === filteredDocs.length && filteredDocs.length > 0 ? 'Alle abwählen' : 'Alle auswählen'}
            </button>
            <button
              onClick={fetchDocuments}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Aktualisieren"
            >
              <ArrowPathIcon className={`h-5 w-5 text-gray-500 dark:text-gray-400 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Document List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-info-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-error-600 dark:text-error-400">{error}</p>
            </div>
          ) : filteredDocs.length === 0 ? (
            <div className="text-center py-12">
              <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                {searchTerm ? 'Keine Dokumente gefunden' : 'Keine unverknüpften Dokumente vorhanden'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredDocs.map((doc) => (
                <div
                  key={doc.id}
                  onClick={() => handleDocumentToggle(doc)}
                  className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    selectedDocuments.has(doc.id)
                      ? 'bg-info-50 dark:bg-info-900/20 border-info-500 shadow-md'
                      : doc.is_linked || (doc.linked_bookings && doc.linked_bookings.length > 0)
                      ? 'bg-gray-50 dark:bg-gray-900 border-gray-300 dark:border-gray-600 opacity-75'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-info-300 dark:hover:border-info-700 hover:shadow-md'
                  }`}
                >
                  {/* Selection Indicator or Linked Badge */}
                  {(doc.is_linked || (doc.linked_bookings && doc.linked_bookings.length > 0)) ? (
                    <div className="absolute top-3 right-3 px-2 py-1 bg-gray-500 text-white rounded-full text-xs font-medium flex items-center gap-1">
                      <LinkIcon className="h-3 w-3" />
                      <span>Verknüpft</span>
                    </div>
                  ) : (
                    <div className={`absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                      selectedDocuments.has(doc.id)
                        ? 'bg-info-500 border-info-500'
                        : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                    }`}>
                      {selectedDocuments.has(doc.id) && (
                        <CheckIcon className="h-4 w-4 text-white" />
                      )}
                    </div>
                  )}

                  <div className="flex items-start gap-3">
                    {/* Document Icon */}
                    <div className="flex-shrink-0 w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                      {getDocumentIcon(doc.file_path)}
                    </div>

                    {/* Document Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {doc.name}
                      </h3>

                      <div className="mt-2 space-y-1">
                        {doc.invoice_amount !== undefined && doc.invoice_amount !== null && doc.invoice_amount !== 0 && (
                          <div className="flex items-center gap-1 text-sm">
                            <CurrencyEuroIcon className="h-4 w-4 text-success-600" />
                            <span className="text-success-600 font-bold">
                              {formatCurrency(doc.invoice_amount)}
                            </span>
                          </div>
                        )}

                        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <CalendarIcon className="h-3.5 w-3.5" />
                          <span>{formatDate(doc.created_at)}</span>
                        </div>

                        {doc.member_name && (
                          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <UserIcon className="h-3.5 w-3.5" />
                            <span className="truncate">{doc.member_name}</span>
                          </div>
                        )}

                        {doc.tag && (
                          <div className="flex items-center gap-1 text-xs">
                            <TagIcon className="h-3.5 w-3.5 text-info-500" />
                            <span className="text-info-600 dark:text-info-400">{doc.tag}</span>
                          </div>
                        )}

                        {doc.ai_comment && (
                          <p className="text-xs text-primary-600 dark:text-primary-400 italic line-clamp-2">
                            {doc.ai_comment}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {selectedDocuments.size > 0
                  ? `${selectedDocuments.size} Dokument${selectedDocuments.size !== 1 ? 'e' : ''} ausgewählt`
                  : 'Wählen Sie Dokumente zum Verknüpfen aus'
                }
              </p>
              {selectedAmount > 0 && (
                <p className="text-xs font-medium">
                  <span className="text-gray-500">Summe: </span>
                  <span className="text-info-600 dark:text-info-400">{formatCurrency(selectedAmount)}</span>
                  {initialAmount !== 0 && (
                    <span className={`ml-2 ${
                      remainingAmount === 0
                        ? 'text-success-600 dark:text-success-400'
                        : remainingAmount > 0
                        ? 'text-warning-600 dark:text-warning-400'
                        : 'text-error-600 dark:text-error-400'
                    }`}>
                      ({remainingAmount === 0
                        ? 'Passt genau'
                        : remainingAmount > 0
                        ? `Fehlt: ${formatCurrency(remainingAmount)}`
                        : `Über: ${formatCurrency(Math.abs(remainingAmount))}`
                      })
                    </span>
                  )}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleConfirm}
                disabled={selectedDocuments.size === 0}
                className={`px-6 py-2 rounded-lg font-medium transition-all ${
                  selectedDocuments.size > 0
                    ? 'bg-info-600 hover:bg-info-700 text-white shadow-md hover:shadow-lg'
                    : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                }`}
              >
                Verknüpfen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceMatcher;